/**
 * RESPONSIBILITY: the @List control — where JSONForms hands a repeatable to us.
 *
 * The rows themselves live in repeatable.tsx, because a list inside a row must behave
 * exactly like this one; how a column is DRAWN lives in list-cells.tsx. All this file
 * owns is the binding: read the annotation off the UI Schema, the vocabulary off the
 * JSON Schema, and hand both to the control.
 *
 * Registers with a tester + rank. The highest-ranked renderer for a schema node
 * wins, so adding a control is a registration — there is no switch on field type.
 */
import { rankWith, optionIs, type ControlProps } from '@jsonforms/core';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { Repeatable } from './repeatable';
import type { ItemField } from './cell-types';

function ListControlBase({ data, handleChange, path, label, uischema, schema }: ControlProps) {
  const opts = (uischema as { options?: Record<string, unknown> }).options ?? {};
  const fields = (opts.itemFields ?? []) as ItemField[];
  const rows: Record<string, unknown>[] = Array.isArray(data) ? data : [];
  const maxItems = (schema as { maxItems?: number }).maxItems;

  // A column with `in` resolves its vocabulary against the section root, which a row
  // cannot see from inside itself.
  const root = (useJsonForms().core?.data ?? {}) as Record<string, unknown>;

  // A fixed vocabulary is an enum in the data schema. Read once, by key, rather than
  // per cell: the schema is the same object for every row.
  const props = (schema as { items?: { properties?: Record<string, { enum?: string[] }> } })
    .items?.properties ?? {};
  const enums = Object.fromEntries(fields.map((f) => [f.key, props[f.key]?.enum]));

  return (
    <div className="ctl">
      <label className="ctl-label">{label}</label>
      <Repeatable
        path={path} fields={fields} rows={rows} root={root} enums={enums}
        maxItems={maxItems}
        onChange={(next) => handleChange(path, next)}
      />
    </div>
  );
}

export const listControl = {
  tester: rankWith(10, optionIs('widget', 'list')),
  renderer: withJsonFormsControlProps(ListControlBase),
};
